import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
    setShortcuts,
    addShortcut,
    removeShortcut,
    Shortcut,
} from '@/store/slices/shortcutsSlice'
import {
    getShortcuts,
    createShortcut,
    deleteShortcut,
} from '@/lib/api/utils/shortcuts-api'
import { MdBookmarks, MdClose, MdAdd, MdLink } from 'react-icons/md'

// Mapeamento de paths para titulos sugeridos
const pathTitles: Record<string, string> = {
    '/admin': 'Dashboard',
    '/atendimento/chats': 'Atendimentos',
    '/atendimento/sectors': 'Setores',
    '/atendimento/agents': 'Atendentes',
    '/hr/employees': 'Funcionarios',
    '/account': 'Minha Conta',
    '/atendimento/agenda': 'Agenda',
    '/core/internal-chat': 'Chat Interno',
    '/person/clients': 'Clientes',
    '/core/reports': 'Relatorios',
    '/settings': 'Configuracoes',
    '/atendimento/integrations': 'Integracoes',
    '/ia': 'Inteligencia Artificial',
}

const getSuggestedTitle = (path: string): string => {
    // Tenta match exato
    if (pathTitles[path]) return pathTitles[path]

    // Tenta match parcial (ex: /produtos/123 -> Produtos)
    const segments = path.split('/').filter(Boolean)
    if (segments.length > 0) {
        const basePath = '/' + segments[0]
        if (pathTitles[basePath]) {
            // Se tem ID, adiciona ao titulo
            if (
                segments.length > 1 &&
                /^\d+$/.test(segments[segments.length - 1])
            ) {
                return `${pathTitles[basePath]} #${segments[segments.length - 1]}`
            }
            return pathTitles[basePath]
        }
    }

    // Fallback: formata o path
    if (path === '/') return 'Inicio'
    return (
        path
            .split('/')
            .filter(Boolean)
            .map(s => s.charAt(0).toUpperCase() + s.slice(1))
            .join(' > ') || 'Atalho'
    )
}

interface ShortcutsDropdownProps {
    /**
     * `button`: botão próprio na navbar (uso histórico).
     * `menu-item`: linha dentro do dropdown da foto do usuário, com a lista abrindo
     * para o lado.
     *
     * Os atalhos saíram da barra em 30/09/2026: ela estava lotada, e atalho é coisa
     * que se configura uma vez e se usa de vez em quando — não merecia lugar fixo ao
     * lado do que a operação olha o dia inteiro.
     */
    variant?: 'button' | 'menu-item'
}

export default function ShortcutsDropdown({ variant = 'button' }: ShortcutsDropdownProps) {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const shortcuts = useAppSelector(
        (state: any) => state.shortcuts?.shortcuts || []
    )

    const [showAddForm, setShowAddForm] = useState(false)
    const [newTitle, setNewTitle] = useState('')
    const [loading, setLoading] = useState(false)

    const currentPath = location.pathname

    // Verifica se a URL atual ja esta nos atalhos
    const isCurrentPathSaved = shortcuts.some(
        (s: Shortcut) => s.path === currentPath
    )

    // Carrega atalhos ao montar
    useEffect(() => {
        loadShortcuts()
    }, [])

    // Atualiza o titulo sugerido quando o path muda
    useEffect(() => {
        setNewTitle(getSuggestedTitle(currentPath))
    }, [currentPath])

    const loadShortcuts = async () => {
        const response = await getShortcuts()
        if (response.success && response.result) {
            dispatch(setShortcuts(response.result as Shortcut[]))
        }
    }

    const handleAddShortcut = async () => {
        if (!newTitle.trim() || loading) return

        setLoading(true)
        const response = await createShortcut({
            title: newTitle.trim(),
            path: currentPath,
        })
        setLoading(false)

        if (response.success && response.result) {
            dispatch(addShortcut(response.result as Shortcut))
            setShowAddForm(false)
            setNewTitle(getSuggestedTitle(currentPath))
        }
    }

    const handleRemoveShortcut = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation()

        const response = await deleteShortcut(id)
        if (response.success) {
            dispatch(removeShortcut(id))
        }
    }

    const handleNavigate = (path: string) => {
        navigate(path)
        closeDropdown()
    }

    const closeDropdown = () => {
        const dropdown = document.activeElement as HTMLElement
        if (dropdown) dropdown.blur()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAddShortcut()
        }
    }

    return (
        <div
            className={
                variant === 'menu-item'
                    ? // Submenu do dropdown da foto: abre para a ESQUERDA, senão a
                      // lista de 320px sai da tela — o menu da conta já está colado
                      // na borda direita.
                      //
                      // SEM `dropdown-end`: ele alinharia a BASE do painel com o item,
                      // jogando o conteúdo para cima e cortando o cabeçalho no topo da
                      // janela. Sem ele, o painel começa na linha do item e desce.
                      'dropdown dropdown-left w-full'
                    : 'dropdown dropdown-bottom dropdown-end max-sm:!static'
            }
        >
            {variant === 'menu-item' ? (
                <div tabIndex={0} role="button" className="flex w-full items-center gap-2">
                    <MdBookmarks className="text-lg" /> Meus Atalhos
                    {shortcuts.length > 0 && (
                        <span className="badge badge-secondary badge-sm ml-auto">
                            {shortcuts.length}
                        </span>
                    )}
                </div>
            ) : (
                <div
                    tabIndex={0}
                    role="button"
                    className="btn btn-ghost btn-circle relative"
                    title="Meus Atalhos"
                >
                    <MdBookmarks size={22} />
                    {shortcuts.length > 0 && (
                        <span className="badge badge-secondary badge-xs absolute -top-1 -right-1">
                            {shortcuts.length}
                        </span>
                    )}
                </div>
            )}

            <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[100] w-80 max-sm:!inset-x-2 max-sm:!w-auto max-h-96 overflow-hidden p-0 shadow-lg border border-base-200 mt-2"
            >
                {/* Cabecalho */}
                <li className="px-4 py-3 border-b border-base-300 bg-base-200/50">
                    <span className="font-semibold text-sm p-0 hover:bg-transparent cursor-default">
                        Meus Atalhos
                    </span>
                </li>

                {/* Lista de atalhos */}
                <div className="max-h-52 overflow-y-auto">
                    {shortcuts.length === 0 ? (
                        <li className="px-4 py-6 text-center text-base-content/60">
                            <div className="flex flex-col items-center p-0 hover:bg-transparent cursor-default">
                                <MdBookmarks className="text-3xl mb-2 opacity-50" />
                                <p className="text-sm">Nenhum atalho salvo</p>
                                <p className="text-xs mt-1">
                                    Adicione atalhos para acessar suas paginas
                                    favoritas rapidamente
                                </p>
                            </div>
                        </li>
                    ) : (
                        shortcuts.map((shortcut: Shortcut) => (
                            <li key={shortcut.id}>
                                <div
                                    onClick={() =>
                                        handleNavigate(shortcut.path)
                                    }
                                    className="flex items-center justify-between px-4 py-2.5 hover:bg-base-200 cursor-pointer group rounded-none"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <MdLink className="text-base-content/50 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">
                                                {shortcut.title}
                                            </p>
                                            <p className="text-xs text-base-content/50 truncate">
                                                {shortcut.path}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={e =>
                                            handleRemoveShortcut(e, shortcut.id)
                                        }
                                        className="btn btn-ghost btn-xs btn-circle opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remover atalho"
                                    >
                                        <MdClose className="text-base" />
                                    </button>
                                </div>
                            </li>
                        ))
                    )}
                </div>

                {/* Separador */}
                <div className="border-t border-base-300" />

                {/* Secao de adicionar */}
                <div className="p-3 bg-base-200/30">
                    {!showAddForm ? (
                        <button
                            type="button"
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                setShowAddForm(true)
                            }}
                            onMouseDown={e => e.preventDefault()}
                            disabled={isCurrentPathSaved}
                            className={`btn btn-sm w-full gap-2 ${
                                isCurrentPathSaved
                                    ? 'btn-disabled'
                                    : 'btn-secondary btn-outline'
                            }`}
                        >
                            <MdAdd className="text-lg" />
                            {isCurrentPathSaved
                                ? 'Pagina ja adicionada'
                                : 'Adicionar esta pagina'}
                        </button>
                    ) : (
                        <div className="space-y-2 p-0">
                            <div className="text-xs text-base-content/70 mb-1">
                                Pagina atual:{' '}
                                <span className="font-mono">{currentPath}</span>
                            </div>
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="Titulo do atalho"
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onMouseDown={e => e.stopPropagation()}
                                autoFocus
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setShowAddForm(false)
                                    }}
                                    onMouseDown={e => e.preventDefault()}
                                    className="btn btn-ghost btn-sm flex-1"
                                    disabled={loading}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleAddShortcut()
                                    }}
                                    onMouseDown={e => e.preventDefault()}
                                    disabled={!newTitle.trim() || loading}
                                    className="btn btn-secondary btn-sm flex-1"
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        'Adicionar'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </ul>
        </div>
    )
}
